import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useRemovalPreview } from '@/features/permissions/hooks/useRemovalPreview';
import { Spinner, Text, VStack } from '@/ui';

type Props = {
  /** The collaborator being removed, or null when the dialog is closed. */
  user: { id: string; name?: string | null; email?: string | null } | null;
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

// Removing a collaborator can quietly stop events arriving. A source is held on
// behalf of whoever added it, so the ones this person added move to the person
// doing the removing, and any the remover cannot read themselves go dark. Name
// those up front rather than letting them discover it in the Sources panel.
export const RemoveCollaboratorDialog = ({
  user,
  isLoading,
  onConfirm,
  onCancel,
}: Props) => {
  const { feedsGoingDark, isPending, isError } = useRemovalPreview(
    user?.id ?? null
  );
  const who = user?.name || user?.email || 'this person';

  return (
    <ConfirmDialog
      confirmButtonText="Remove"
      // Removing before the check has finished would skip the warning it exists
      // to show, so the button waits for it.
      isConfirmDisabled={isPending}
      isLoading={isLoading}
      isOpen={user !== null}
      title={`Remove ${who}?`}
      onCancel={onCancel}
      onConfirm={onConfirm}
    >
      <Text>They will lose access to this schedule.</Text>

      {isPending && <Spinner color="magicPurple.500" mt={3} size="sm" />}

      {/* The check is advisory, so a failure is disclosed rather than
          silently treated as "nothing affected". Removal stays possible. */}
      {isError && (
        <Text color="ink.soft" mt={3}>
          We could not check which sources this affects. Any source they added
          that you cannot read yourself will stop showing events.
        </Text>
      )}

      {!isPending && !isError && feedsGoingDark.length > 0 && (
        <>
          <Text mt={3}>
            These sources will stop showing events, because you do not have
            access to them yourself. Their settings stay saved.
          </Text>
          {/* The dialog body is centred, so a bulleted list would leave its
              markers stranded at the left edge. Plain bold lines read better. */}
          <VStack mt={2} spacing={1}>
            {feedsGoingDark.map((feed) => (
              <Text fontWeight={600} key={feed.id}>
                {feed.title}
              </Text>
            ))}
          </VStack>
        </>
      )}
    </ConfirmDialog>
  );
};
